import { Copy, RotateCcw, Save, Lock, Trash2 } from 'lucide-react'
import { clsx } from 'clsx'
import type { AccountingSnapshot, FormationResult } from '../types'
import styles from './SquadPickerHeader.module.css'

type SquadPickerHeaderProps = {
  formation:          FormationResult
  playerCount:        number
  budgetRemaining:    number
  totalFantasyPoints: number
  gwPoints:           number
  isWindowOpen:       boolean
  canSave:            boolean
  isSaving:           boolean
  isDirty:            boolean
  accounting:         AccountingSnapshot
  canCopy:            boolean   // user has 2+ leagues in same competition AND window open
  onCopy:             () => void
  onSave:             () => void
  onReset:            () => void
  onClear:            () => void
}

function formatBudget(value: number): string {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  return `${sign}$${abs.toFixed(1)}`
}

export function SquadPickerHeader({
  formation,
  playerCount,
  budgetRemaining,
  totalFantasyPoints,
  gwPoints,
  isWindowOpen,
  canSave,
  isSaving,
  isDirty,
  accounting,
  canCopy,
  onCopy,
  onSave,
  onReset,
  onClear,
}: SquadPickerHeaderProps) {
  const formationLabel = formation.valid ? formation.formation : '?'
  const isOverBudget   = budgetRemaining < 0
  const showAccounting = accounting.hasBaseline
  const isUnlimited    = accounting.transferOption === 'OPTION_B'
  const hasPenalty     = accounting.penaltyPoints < 0

  return (
    <div className={styles.header}>
      {/* Transfer window badge */}
      <div className={clsx(styles.windowBadge, isWindowOpen ? styles.open : styles.closed)}>
        {isWindowOpen ? (
          <>
            <span className={styles.windowDot} aria-hidden="true" />
            Janela aberta
          </>
        ) : (
          <>
            <Lock size={11} strokeWidth={2.5} />
            Janela fechada
          </>
        )}
      </div>

      {/* Formation chip */}
      <div className={clsx(styles.formationChip, !formation.valid && styles.formationInvalid)}>
        <span className={styles.formationLabel}>Formação</span>
        <span className={styles.formationValue}>{formationLabel}</span>
      </div>

      {/* Player count */}
      <div className={styles.countChip}>
        <span className={styles.countValue}>{playerCount}</span>
        <span className={styles.countOf}>/15</span>
      </div>

      {/* Budget */}
      <div className={clsx(styles.budgetChip, isOverBudget && styles.budgetOver)}>
        <span className={styles.budgetLabel}>Saldo</span>
        <span className={styles.budgetValue}>{formatBudget(budgetRemaining)}</span>
      </div>

      {/* Current GW points */}
      <div className={styles.pointsChip}>
        <span className={styles.pointsLabel}>
          <span className={styles.labelFull}>Rodada</span>
          <span className={styles.labelShort}>Rod</span>
        </span>
        <span className={styles.pointsValue}>{gwPoints}</span>
      </div>

      {/* Cumulative total points */}
      <div className={styles.pointsChip}>
        <span className={styles.pointsLabel}>Total</span>
        <span className={styles.pointsValue}>{totalFantasyPoints}</span>
      </div>

      {/* Transfer accounting — hidden pre-first-lock (baseline empty) */}
      {showAccounting && (
        <>
          {/* Transfers used / remaining */}
          <div className={styles.transferChip} title="Transferências usadas">
            <span className={styles.transferChipLabel}>Transf.</span>
            <span className={styles.transferChipValue}>
              {isUnlimited
                ? `${accounting.outgoingTotal} / ∞`
                : `${accounting.freeTransfersUsed} / 3`}
            </span>
          </div>

          {/* Elimination credits remaining — only when the league grants any */}
          {accounting.eliminationCreditsRemaining + accounting.eliminationCreditsUsed > 0 && (
            <div
              className={clsx(
                styles.transferChip,
                accounting.eliminationCreditsRemaining > 0 && styles.creditHighlight,
              )}
              title="Créditos de eliminação restantes"
            >
              <span className={styles.transferChipLabel}>Elim.</span>
              <span className={styles.transferChipValue}>
                {accounting.eliminationCreditsRemaining}
              </span>
            </div>
          )}

          {/* Bye credits remaining — only when the league grants any */}
          {accounting.byeCreditsRemaining + accounting.byeCreditsUsed > 0 && (
            <div
              className={clsx(
                styles.transferChip,
                accounting.byeCreditsRemaining > 0 && styles.creditHighlight,
              )}
              title="Créditos de folga restantes"
            >
              <span className={styles.transferChipLabel}>Folga</span>
              <span className={styles.transferChipValue}>
                {accounting.byeCreditsRemaining}
              </span>
            </div>
          )}

          {/* Penalty — only when a penalty would apply on save */}
          {hasPenalty && (
            <div
              className={clsx(styles.transferChip, styles.penaltyWarn)}
              title={`${accounting.paidExtras} transferência(s) paga(s) — ${accounting.penaltyPoints} pts`}
            >
              <span className={styles.transferChipLabel}>Multa</span>
              <span className={styles.transferChipValue}>{accounting.penaltyPoints}</span>
            </div>
          )}
        </>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        <button
          className={styles.resetBtn}
          onClick={onReset}
          disabled={!isDirty}
          aria-label="Desfazer alterações"
          title="Desfazer alterações"
        >
          <RotateCcw size={14} />
        </button>
        <button
          className={styles.clearBtn}
          onClick={onClear}
          disabled={!isWindowOpen || playerCount === 0}
          aria-label="Resetar — remover todos os jogadores"
          title="Resetar — remover todos os jogadores"
        >
          <Trash2 size={14} />
          <span className={styles.clearBtnLabel}>Resetar</span>
        </button>
        {canCopy && (
          <button
            className={styles.copyBtn}
            onClick={onCopy}
            disabled={isSaving}
            aria-label="Copiar de outra liga"
            title="Copiar de outra liga"
          >
            <Copy size={14} />
            <span className={styles.copyBtnLabel}>Copiar de outra liga</span>
          </button>
        )}
        <button
          className={styles.saveBtn}
          onClick={onSave}
          disabled={!canSave}
          aria-label="Salvar escalação"
        >
          {isSaving ? (
            <span className={styles.savingSpinner} aria-hidden="true" />
          ) : (
            <Save size={14} />
          )}
          <span className={styles.saveBtnLabel}>{isSaving ? 'Salvando…' : 'Salvar'}</span>
        </button>
      </div>
    </div>
  )
}
